import React, { useCallback, useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import Tabs from "../../widgets/tabs";
import LoaderTrans from "../../widgets/loadertrans";
import Table from "../../widgets/table";
import Map from "../../img/map-pin.svg";
import ErrorCard from "../../widgets/error";
import {
  ERROR_MESSAGE,
  NODATA_ERROR_MESSAGE,
} from "../../constants/dialog.constants";
import {
  BENCHMARK_SCHEDULING,
  BENCHMARK_SCHEDULINGCOMPLETED,
  DATE_FORMAT_UPDATED
} from "../../constants/app.constants";
import {
  fetchBenchmarkScheduledJobs,
  fetchBenchmarkCompletedJobs,
  confirmHoldScheduled,
} from "../scheduling/scheduling.utils";
import { headerType } from "../../widgets/table";
import SchedulingApprovalModal from "./schedulingApprovalModal";
import SchedulingRequestChangeModal from "./schedulingRequestChangeModal";
import SchedulingMapChangeModal from "./schedulingMapModal";
import SchedulingAddPropertyModal from "./schedulingAddPropertyModal";
import SchedulingReadyForMulchModal from "./schedulingReadyForMulchModal";
import SchedulingPriorityModal from "./schedulingPriorityModal";
import SchedulingMoveFromListModal from "./schedulingMoveFromListModal";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import dayjs, { Dayjs } from "dayjs";
import Swal from "sweetalert2";

const tabData = [
  { label: "Overview", path: "Overview" },
  { label: "Client Connection", path: "ClientConnection" },
  { label: "Invoicing", path: "Invoicing" },
  { label: "Scheduling", path: "Scheduling" },
  { label: "Quotes", path: "Quotes" },
];

export interface ButtonProps {
  btnClassName: string;
  btnName: string;
  btnType?: string;
  btnSrc?: string;
  btnColHeading?: string;
  alwaysShow?: boolean;
}

type SelectedScheduledProperty = {
  id: number;
};

const colHeaders: headerType[] = [
  { label: "ID" },
  { label: "Scheduled Date" },
  { label: "Property" },
  { label: "Estimated Yards" },
  {
    label: "% Complete",
    style: { alignment: { textAlign: "center", verticalAlign: "middle", } }

  },
  { label: "Product/Color" },
];

const colHeadersCompleted: headerType[] = [
  { label: "Completed Date", exlStyle: { width: 15 } },
  { label: "Property", exlStyle: { width: 30 } },
  { label: "Installed Yards", exlStyle: { width: 12 } },
  { label: "Invoice #", exlStyle: { width: 12 } },
  { label: "Invoice Date", exlStyle: { width: 12 } },
  { label: "Product/Color", exlStyle: { width: 20 } },
];

const Scheduling: React.FC = () => {
  const [isModalApproveOpen, setIsModalApproveOpen] = useState(false);
  const [isModalRequestOpen, setIsModalRequestOpen] = useState(false);
  const [isModalMapOpen, setIsModalMapOpen] = useState(false);
  const [isAddPropertyModalOpen, setIsAddPropertyModalOpen] = useState(false);
  const [isEditPropertyModalOpen, setIsEditPropertyModalOpen] = useState(false);
  const [approveId, setApproveId] = useState(0);
  const [requestChangeId, setRequestChangeId] = useState(0);
  const [mapId, setMapId] = useState(0);
  const [selectedScheduledProperty, setSelectedScheduledProperty] =
    useState<SelectedScheduledProperty | null>(null);
  const [dateError, setDateError] = useState<string>("");
  const [scheduledSearchText, setScheduledSearchText] = useState("");
  const [scheduledDateError, setScheduledDateError] = useState<string>("");
  const [scheduledStartDate, setScheduledStartDate] = useState<Dayjs | null>(null);
  const [scheduledEndDate, setScheduledEndDate] = useState<Dayjs | null>(null);
  const [scheduledDateKey, setScheduledDateKey] = useState(0);

  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [isMapTrue, setIsMapTrue] = useState(false);
  const [isRequestChangeTrue, setIsRequestChangeTrue] = useState(false);
  const [isApproveTrue, setIsApproveTrue] = useState(false);
  const [isReadyForMulchModalOpen, setIsReadyForMulchModalOpen] = useState(false);
  const [readyForMulchPropertyName, setReadyForMulchPropertyName] = useState("");
  const [loadId, setLoadId] = useState(0);
  const [isPriorityModalOpen, setIsPriorityModalOpen] = useState(false);
  const [priorityPropertyName, setPriorityPropertyName] = useState("");
  const [isMoveFromListModalOpen, setIsMoveFromListModalOpen] = useState(false);
  const [moveFromListPropertyName, setMoveFromListPropertyName] = useState("");
  const [isConfirmingHold, setIsConfirmingHold] = useState(false);

  const [dateKey, setDateKey] = useState(0);

  const {
    isLoading: isLoadingBenchmarkScheduledJobs,
    error: errorBenchmarkScheduledJobs,
    data: dataBenchmarkScheduledJobs,
    isFetching: isFetchingBenchmarkScheduledJobs,
    refetch: refetchScheduledJobs,
  } = useQuery({
    queryKey: [
      BENCHMARK_SCHEDULING,
      scheduledStartDate?.isValid() ? scheduledStartDate.toISOString() : null,
      scheduledEndDate?.isValid() ? scheduledEndDate.toISOString() : null,
    ],
    queryFn: () =>
      fetchBenchmarkScheduledJobs(
        localStorage.getItem("ID") ?? "",
        scheduledStartDate?.isValid() ? scheduledStartDate.toDate() : undefined,
        scheduledEndDate?.isValid() ? scheduledEndDate.toDate() : undefined
      ),
    enabled:
      !scheduledStartDate ||
      !scheduledEndDate ||
      !scheduledStartDate.isAfter(scheduledEndDate),
    refetchOnWindowFocus: false,
  });

  const {
    isLoading: isLoadingBenchmarkCompletedJobs,
    error: errorBenchmarkCompletedJobs,
    data: dataBenchmarkCompletedJobs,
    isFetching: isFetchingBenchmarkCompletedJobs,
    refetch,
  } = useQuery({
    queryKey: [BENCHMARK_SCHEDULINGCOMPLETED, startDate, endDate],
    queryFn: () =>
      fetchBenchmarkCompletedJobs(
        localStorage.getItem("ID") ?? "",
        startDate?.toDate(),
        endDate?.toDate()
      ),
    enabled: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (startDate && endDate) {
      if (startDate.isAfter(endDate)) {
        setDateError("Start date cannot be greater than end date.");
      } else {
        setDateError("");
        refetch();
      }
    } else {
      setDateError("");
    }
  }, [startDate, endDate, refetch]);

  const onResetButton = () => {
    setStartDate(null);
    setEndDate(null);
    // force both datepickers to remount → clears ANY invalid text
    setDateKey(prev => prev + 1);
    setDateError("");
    refetch();
  };

  const onResetScheduledFilters = () => {
    setScheduledSearchText("");
    setScheduledStartDate(null);
    setScheduledEndDate(null);
    setScheduledDateKey((prev) => prev + 1);
    setScheduledDateError("");
  };

  useEffect(() => {
    const isValidRange =
      !startDate || !endDate || startDate.isBefore(endDate) || startDate.isSame(endDate);
    if (isValidRange) {
      refetch();
    }
  }, [startDate, endDate, isApproveTrue, isMapTrue, isRequestChangeTrue]);

  useEffect(() => {
    if (scheduledStartDate && scheduledEndDate && scheduledStartDate.isAfter(scheduledEndDate)) {
      setScheduledDateError("Start date cannot be greater than end date.");
      return;
    }

    setScheduledDateError("");
  }, [scheduledStartDate, scheduledEndDate]);

  const onClickApprovalScheduling = useCallback((id: number) => {
    setIsModalApproveOpen(true);
    setApproveId(id);
  }, []);

  const onClickRequestChangeScheduling = useCallback((id: number) => {
    setIsModalRequestOpen(true);
    setRequestChangeId(id);
  }, []);

  const onClickMapScheduling = useCallback((id: number) => {
    setIsModalMapOpen(true);
    setMapId(id);
  }, []);

  const onClickReadyForMulchScheduling = useCallback(
    (id: number) => {
      const selectedRow = dataBenchmarkScheduledJobs?.find(
        (row: string[]) => parseInt(row[0]) === id
      );

      setLoadId(id);
      setReadyForMulchPropertyName(
        (selectedRow?.[2] ?? "").toString().replace(/<[^>]*>/g, "").trim()
      );
      setIsReadyForMulchModalOpen(true);
    },
    [dataBenchmarkScheduledJobs]
  );

  const onClickPriorityScheduling = useCallback(
    (id: number) => {
      const selectedRow = dataBenchmarkScheduledJobs?.find(
        (row: string[]) => parseInt(row[0]) === id
      );

      setLoadId(id);
      setPriorityPropertyName(
        (selectedRow?.[2] ?? "").toString().replace(/<[^>]*>/g, "").trim()
      );
      setIsPriorityModalOpen(true);
    },
    [dataBenchmarkScheduledJobs]
  );

  const onClickHoldScheduling = useCallback(
    async (id: number) => {
      try {
        setIsConfirmingHold(true);
        const result = await confirmHoldScheduled(id);
        const isSuccessful =
          result === "Success" ||
          result?.messages?.[0]?.code === "0" ||
          result?.messages?.[0]?.code === 0 ||
          result?.status === "success";

        if (isSuccessful) {
          await refetchScheduledJobs();
          Swal.fire({
            customClass: {
              popup: "sweetAlert",
            },
            icon: "success",
            showConfirmButton: false,
            timer: 1500,
            title: "Hold confirmed",
          });
        } else {
          Swal.fire({
            customClass: ".swal2-confirm",
            icon: "warning",
            title: result?.messages?.[0]?.message || "Failed to confirm hold",
          });
        }
      } catch (error) {
        console.error("Failed to confirm hold", error);
        Swal.fire({
          customClass: ".swal2-confirm",
          icon: "warning",
          title: "Failed to confirm hold",
        });
      } finally {
        setIsConfirmingHold(false);
      }
    },
    [refetchScheduledJobs]
  );

  const onClickMoveFromListScheduling = useCallback(
    (id: number) => {
      const selectedRow = dataBenchmarkScheduledJobs?.find(
        (row: string[]) => parseInt(row[0]) === id
      );

      setLoadId(id);
      setMoveFromListPropertyName(
        (selectedRow?.[2] ?? "").toString().replace(/<[^>]*>/g, "").trim()
      );
      setIsMoveFromListModalOpen(true);
    },
    [dataBenchmarkScheduledJobs]
  );

  const handleModalClose = useCallback(() => {
    setIsModalApproveOpen(false);
    setIsModalRequestOpen(false);
    setIsModalMapOpen(false);
    setIsAddPropertyModalOpen(false);
    setIsEditPropertyModalOpen(false);
    setIsReadyForMulchModalOpen(false);
    setIsPriorityModalOpen(false);
    setIsMoveFromListModalOpen(false);
    setSelectedScheduledProperty(null);
    setReadyForMulchPropertyName("");
    setPriorityPropertyName("");
    setMoveFromListPropertyName("");
    setLoadId(0);
  }, []);

  const handleSubmit = useCallback(() => {
    setIsMapTrue(true);
    setIsRequestChangeTrue(true);
    setIsApproveTrue(true);
    setIsModalMapOpen(false);
    setIsModalRequestOpen(false);
    setIsModalApproveOpen(false);
  }, []);

  const handleReadyForMulchSubmit = useCallback(async () => {
    await refetchScheduledJobs();
    setIsReadyForMulchModalOpen(false);
    setReadyForMulchPropertyName("");
    setLoadId(0);
  }, [refetchScheduledJobs]);

  const handlePrioritySubmit = useCallback(async () => {
    await refetchScheduledJobs();
    setIsPriorityModalOpen(false);
    setPriorityPropertyName("");
    setLoadId(0);
  }, [refetchScheduledJobs]);

  const handleMoveFromListSubmit = useCallback(async () => {
    await refetchScheduledJobs();
    setIsMoveFromListModalOpen(false);
    setMoveFromListPropertyName("");
    setLoadId(0);
  }, [refetchScheduledJobs]);

  const buttonsClass: ButtonProps[] = [

    // 1 → Map button
    {
      btnClassName: "imgIcon m-1",
      btnName: "Map",
      btnType: "Image",
      btnSrc: Map,
      btnColHeading: "Map",
    },

    // 2 → Request Change button
    {
      btnClassName: "btn btn-sm requestChange cancelPwdBtn m-1",
      btnName: "Request Change",
      btnType: "Button",
      btnColHeading: "Actions",
    },
    
  ];

  const filteredScheduledJobs = useMemo(() => {
    if (!dataBenchmarkScheduledJobs) {
      return [];
    }

    const normalizedSearchText = scheduledSearchText.trim().toLowerCase();
    const hasScheduledDateRange = !!scheduledStartDate || !!scheduledEndDate;
    const hasValidScheduledDateRange =
      !scheduledStartDate ||
      !scheduledEndDate ||
      scheduledStartDate.isBefore(scheduledEndDate) ||
      scheduledStartDate.isSame(scheduledEndDate);

    if (!normalizedSearchText && (!hasScheduledDateRange || !hasValidScheduledDateRange)) {
      return dataBenchmarkScheduledJobs;
    }

    return dataBenchmarkScheduledJobs.filter((row: string[]) => {
      const matchesSearch = row
        .slice(0, colHeaders.length)
        .some((cell: string) =>
          (cell ?? "").toString().toLowerCase().includes(normalizedSearchText)
        );

      if (normalizedSearchText && !matchesSearch) {
        return false;
      }

      if (!hasScheduledDateRange || !hasValidScheduledDateRange) {
        return true;
      }

      const scheduledDateValue = row[1];
      const scheduledDate = dayjs(scheduledDateValue);

      if (!scheduledDate.isValid()) {
        return false;
      }

      const isAfterStart =
        !scheduledStartDate ||
        scheduledDate.startOf("day").isSame(scheduledStartDate.startOf("day")) ||
        scheduledDate.isAfter(scheduledStartDate.startOf("day"));
      const isBeforeEnd =
        !scheduledEndDate ||
        scheduledDate.startOf("day").isSame(scheduledEndDate.startOf("day")) ||
        scheduledDate.isBefore(scheduledEndDate.endOf("day"));

      return isAfterStart && isBeforeEnd;
    });
  }, [dataBenchmarkScheduledJobs, scheduledEndDate, scheduledSearchText, scheduledStartDate]);

  const onClickEditPropertyScheduling = useCallback(
    (id: number) => {
      const selectedRow = filteredScheduledJobs.find(
        (row: string[]) => parseInt(row[0]) === id
      );

      setSelectedScheduledProperty(
        selectedRow
          ? {
              id,
            }
          : null
      );
      setIsEditPropertyModalOpen(true);
    },
    [filteredScheduledJobs]
  );


  return (
    <div className="container-fluid quotesContent ">
      <div className="body-content">
        <Tabs tabs={tabData} activeTabIndex={3} />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <div className="d-flex flex-column propertiesContent">
            {!errorBenchmarkScheduledJobs && (
              <div className="">
                <div className="scheduling-header-row">
                  <div className="tblTitle mb-0">Scheduled Jobs</div>
                
                </div>
                <div className="date-filter-container flex-wrap justify-content-between" style={{ marginBottom: "10px" }}>
                  <div className="date-filter-container flex-wrap"><TextField
                    size="small"
                    placeholder="Search scheduled jobs"
                    value={scheduledSearchText}
                    onChange={(event) => setScheduledSearchText(event.target.value)}
                    className="date-input-field"
                    sx={{ width: 240 }}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                  <div className="d-flex gap-2 flex-wrap">
                    <DatePicker
                      key={scheduledDateKey + "-start"}
                      label="Start Date"
                      value={scheduledStartDate}
                      onChange={(newValue) => {
                        setScheduledStartDate(newValue);
                      }}
                      format={DATE_FORMAT_UPDATED}
                      disableFuture
                      slotProps={{
                        textField: {
                          size: "small",
                          className: "date-input-field"
                        }
                      }}
                    />
                    <DatePicker
                      key={scheduledDateKey + "-end"}
                      label="End Date"
                      value={scheduledEndDate}
                      onChange={(newValue) => {
                        setScheduledEndDate(newValue);
                      }}
                      format={DATE_FORMAT_UPDATED}
                      disableFuture
                      slotProps={{
                        textField: {
                          size: "small",
                          className: "date-input-field"
                        }
                      }}
                    />
                  </div>
                  <button
                    className="btn btn-sm resetBtn"
                    onClick={onResetScheduledFilters}
                  >
                    Reset
                  </button>
                  </div>  <button
                    type="button"
                    className="btn btn-sm addPropertyBtn"
                    onClick={() => setIsAddPropertyModalOpen(true)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#fff"><path d="M440-440H200v-80h240v-240h80v240h240v80H520v240h-80v-240Z"/></svg> Add Property
                  </button>
                </div>
                {scheduledDateError && <p className="text-danger">{scheduledDateError}</p>}
                {(isLoadingBenchmarkScheduledJobs || isFetchingBenchmarkScheduledJobs || isConfirmingHold) ? (
                  <LoaderTrans
                    msg={
                      isConfirmingHold
                        ? "Confirming Hold"
                        : "Loading Scheduled Jobs"
                    }
                  />
                ) : filteredScheduledJobs.length > 0 ? (
                  <div className="table-responsive">
                    <Table
                      id="scheduledJobs"
                      classNameTable="tblHeightScheduled"
                      headers={colHeaders}
                      data={filteredScheduledJobs}
                      buttonsClass={buttonsClass}
                      onClickButton1={onClickMapScheduling}
                      onClickButton2={onClickRequestChangeScheduling}
                      onClickButton3={onClickEditPropertyScheduling}
                      onClickButton4={onClickReadyForMulchScheduling}
                      onClickButton5={onClickPriorityScheduling}
                      onClickButton6={onClickMoveFromListScheduling}
                      onClickButton7={onClickHoldScheduling}
                    />
                  </div>
                ) : (
                  <ErrorCard msg={NODATA_ERROR_MESSAGE} />
                )}
              </div>
            )}

            {!isFetchingBenchmarkScheduledJobs && errorBenchmarkScheduledJobs && (
              <ErrorCard msg={ERROR_MESSAGE} />
            )}
          </div>
          <div className="d-flex flex-column propertiesContent">
            <div>
              <div className="tblTitle">Completed Jobs</div>
              <div className="date-filter-container">

                <div className="d-flex gap-2">
                  <DatePicker
                    key={dateKey + "-start"}
                    label="Start Date"
                    value={startDate}
                    onChange={(newValue) => {
                      setStartDate(newValue);
                    }}
                    format={DATE_FORMAT_UPDATED}
                    disableFuture
                    slotProps={{
                      textField: {
                        size: "small",
                        className: "date-input-field"
                      }
                    }}
                  />
                  <DatePicker
                    key={dateKey + "-end"}
                    label="End Date"
                    value={endDate}
                    onChange={(newValue) => {
                      setEndDate(newValue);
                    }}
                    format={DATE_FORMAT_UPDATED}
                    disableFuture
                    slotProps={{
                      textField: {
                        size: "small",
                        className: "date-input-field"
                      }
                    }}
                  />
                </div>
                <button
                  className="btn btn-sm resetBtn"
                  onClick={onResetButton}
                >
                  Reset
                </button>
              </div>

              {dateError && <p className="text-danger">{dateError}</p>}
            </div>

            {(isLoadingBenchmarkCompletedJobs || isFetchingBenchmarkCompletedJobs) && (
              <LoaderTrans msg="Loading Completed Jobs" />
            )}

            {!isLoadingBenchmarkCompletedJobs &&
              !isFetchingBenchmarkCompletedJobs &&
              (errorBenchmarkCompletedJobs ? (
                <ErrorCard msg={ERROR_MESSAGE} />
              ) : !dataBenchmarkCompletedJobs || dataBenchmarkCompletedJobs.length === 0 ? (
                <ErrorCard msg={NODATA_ERROR_MESSAGE} />
              ) : (
                <Table
                  id="completedJobs"
                  classNameTable="tblHeight"
                  headers={colHeadersCompleted}
                  data={dataBenchmarkCompletedJobs}
                  exportExcel
                  fileName="Completed Jobs"
                />
              ))}
          </div>
        </LocalizationProvider>
      </div>

      <SchedulingApprovalModal
        isOpen={isModalApproveOpen}
        onClose={handleModalClose}
        onSubmit={handleSubmit}
        approveId={approveId}
      />
      <SchedulingRequestChangeModal
        isOpen={isModalRequestOpen}
        onClose={handleModalClose}
        onSubmit={handleSubmit}
        requestChangeId={requestChangeId}
      />
      <SchedulingMapChangeModal
        isOpen={isModalMapOpen}
        onClose={handleModalClose}
        onSubmit={handleSubmit}
        mapId={mapId}
      />
      <SchedulingAddPropertyModal
        isOpen={isAddPropertyModalOpen}
        onClose={handleModalClose}
      />
      <SchedulingAddPropertyModal
        isOpen={isEditPropertyModalOpen}
        onClose={handleModalClose}
        isEditMode
        selectedProperty={selectedScheduledProperty}
      />
      <SchedulingReadyForMulchModal
        isOpen={isReadyForMulchModalOpen}
        onClose={handleModalClose}
        onSubmit={handleReadyForMulchSubmit}
        scheduledId={loadId}
        propertyName={readyForMulchPropertyName}
      />
      <SchedulingPriorityModal
        isOpen={isPriorityModalOpen}
        onClose={handleModalClose}
        onSubmit={handlePrioritySubmit}
        scheduledId={loadId}
        propertyName={priorityPropertyName}
      />
      <SchedulingMoveFromListModal
        isOpen={isMoveFromListModalOpen}
        onClose={handleModalClose}
        onSubmit={handleMoveFromListSubmit}
        scheduledId={loadId}
        propertyName={moveFromListPropertyName}
      />
    </div>
  );



};

export default Scheduling;
